/**
 * GBS Quantity Tiers
 *
 * Replaces the Bold Custom Pricing / Quantity Breaks storefront script.
 * Bullion products carry their quantity price breaks as hidden variants
 * whose titles end in "N+" (e.g. "CA19878812 (default) 50+"). This script:
 *
 *  1. Product page: watches the quantity box and switches the form's
 *     variant id (and displayed price) to the matching tier variant.
 *  2. Cart page / checkout click: re-checks every line item and swaps it
 *     to the correct tier variant for its quantity, so quantities changed
 *     in the cart (or accumulated across adds) still get the right price.
 *
 * A product is treated as tiered only when all of its variants except at
 * most one match the "N+" title pattern. Size/colour products (e.g. the
 * merchandise template) never match and are left alone. If the target
 * tier variant is unavailable, the next lower available tier is used, and
 * nothing switches while the base variant is sold out.
 *
 * All stock is on the base variant. Bold leaves the tier variants
 * untracked while the base has stock, so Shopify won't cap a tier order;
 * quantities are capped at the base variant's stock here instead.
 */
(function () {
  'use strict';

  var TIER_RE = /(\d+)\s*\+\s*$/;
  var productCache = {};
  var maxQuantityCache = {};

  function getTiers(variants) {
    if (!variants || variants.length < 2) return null;
    var tiers = [];
    var base = null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var m = TIER_RE.exec(v.title || '');
      if (m) {
        tiers.push({ min: parseInt(m[1], 10), variant: v });
      } else if (!base) {
        base = v;
      } else {
        return null; // two non-tier variants: not a tiered product
      }
    }
    if (!tiers.length) return null;
    if (base) tiers.push({ min: 1, variant: base });
    tiers.sort(function (a, b) { return a.min - b.min; });
    if (tiers[0].min > 1) tiers[0].min = 1;
    return tiers;
  }

  function tierFor(tiers, qty) {
    var chosen = tiers[0];
    // Tier variants are the same stock as the base; if the base is sold out,
    // so is every tier, even before gbs-api has synced their inventory.
    if (chosen.variant.available === false) return chosen;
    for (var i = 0; i < tiers.length; i++) {
      if (qty >= tiers[i].min && tiers[i].variant.available !== false) chosen = tiers[i];
    }
    return chosen;
  }

  function fetchProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  // Most of this product that can be sold: the base variant's stock
  // (templates/product.gbs-stock.liquid), or Infinity when the base doesn't
  // limit sales or the lookup fails.
  function fetchMaxQuantity(handle) {
    if (!maxQuantityCache[handle]) {
      maxQuantityCache[handle] = fetch('/products/' + handle + '?view=gbs-stock')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (stock) {
          if (!stock || stock.inventory_management !== 'shopify' || stock.inventory_policy !== 'deny') return Infinity;
          return Math.max(parseInt(stock.inventory_quantity, 10) || 0, 0);
        })
        .catch(function () { return Infinity; });
    }
    return maxQuantityCache[handle];
  }

  function formatMoney(cents) {
    try {
      if (window.BOLD && BOLD.common && BOLD.common.Shopify && BOLD.common.Shopify.formatMoney) {
        return BOLD.common.Shopify.formatMoney(cents);
      }
    } catch (e) { /* fall through */ }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ---------------- product page ---------------- */

  function initProductPage() {
    var match = location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!match) return;
    var form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;
    var idInput = form.querySelector('[name="id"]');
    var qtyInput = document.querySelector('input.quantity__input[name="quantity"]') ||
                   document.querySelector('input[name="quantity"]');
    if (!idInput || !qtyInput) return;

    Promise.all([fetchProduct(match[1]), fetchMaxQuantity(match[1])]).then(function (results) {
      var product = results[0];
      var maxQty = results[1];
      if (!product) return;
      var tiers = getTiers(product.variants);
      if (!tiers) return;
      var priceEl = document.querySelector('[id^="price-"]');
      // Lets Dawn's + button stop at the stock level too.
      if (isFinite(maxQty) && maxQty > 0) qtyInput.max = maxQty;

      // "Total: $x.xx" line between the quantity box and the buy buttons.
      var totalEl = document.createElement('div');
      totalEl.className = 'gbs-qty-total';
      totalEl.style.cssText = 'margin:1rem 0 0.2rem;font-size:1.6rem;';
      var qtyWrapper = qtyInput.closest('.product-form__quantity') || qtyInput.parentElement;
      qtyWrapper.insertAdjacentElement('afterend', totalEl);

      function apply() {
        var qty = parseInt(qtyInput.value, 10) || 1;
        // A sold-out base (maxQty 0) is left to tierFor and Shopify.
        var capped = maxQty > 0 && qty > maxQty;
        if (capped) {
          qty = maxQty;
          qtyInput.value = maxQty;
        }
        var tier = tierFor(tiers, qty);
        if (String(idInput.value) !== String(tier.variant.id)) {
          idInput.value = tier.variant.id;
          idInput.disabled = false;
        }
        if (priceEl) priceEl.innerHTML = formatMoney(tier.variant.price);
        totalEl.innerHTML = 'Total: <strong>' + formatMoney(qty * tier.variant.price) + '</strong>' +
          (capped ? '<br><small>Only ' + maxQty + ' available</small>' : '');
      }

      qtyInput.addEventListener('change', apply);
      qtyInput.addEventListener('input', apply);
      // Dawn's quantity-input +/- buttons re-dispatch change, but be safe:
      document.addEventListener('click', function (e) {
        if (e.target && e.target.closest && e.target.closest('quantity-input button')) {
          setTimeout(apply, 0);
        }
      });
      apply();
    });
  }

  /* ---------------- cart enforcement ---------------- */

  function enforceTiers() {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) return false;
        var handles = {};
        cart.items.forEach(function (it) { handles[it.handle] = true; });
        var handleList = Object.keys(handles);
        return Promise.all([
          Promise.all(handleList.map(fetchProduct)),
          Promise.all(handleList.map(fetchMaxQuantity))
        ]).then(function (results) {
          var tiersByHandle = {};
          var maxByHandle = {};
          handleList.forEach(function (handle, i) {
            var t = results[0][i] && getTiers(results[0][i].variants);
            if (t) {
              tiersByHandle[handle] = t;
              maxByHandle[handle] = results[1][i];
            }
          });

          // A tiered product's lines (base and any tiers) become one line on
          // the tier for its total quantity, capped at the base stock. If the
          // base has sold out, its lines are removed.
          var current = {};
          var desired = {};
          var totals = {};
          cart.items.forEach(function (it) {
            current[it.variant_id] = (current[it.variant_id] || 0) + it.quantity;
            if (tiersByHandle[it.handle]) {
              totals[it.handle] = (totals[it.handle] || 0) + it.quantity;
            } else {
              desired[it.variant_id] = (desired[it.variant_id] || 0) + it.quantity;
            }
          });
          Object.keys(totals).forEach(function (handle) {
            var qty = Math.min(totals[handle], maxByHandle[handle]);
            if (qty <= 0) return;
            var target = tierFor(tiersByHandle[handle], qty).variant.id;
            desired[target] = (desired[target] || 0) + qty;
          });

          var updates = {};
          var changed = false;
          Object.keys(current).forEach(function (vid) {
            var want = desired[vid] || 0;
            if (want !== current[vid]) { updates[vid] = want; changed = true; }
          });
          Object.keys(desired).forEach(function (vid) {
            if (!(vid in current)) { updates[vid] = desired[vid]; changed = true; }
          });
          if (!changed) return false;

          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates })
          }).then(function (r) { return r.ok; });
        });
      })
      .catch(function () { return false; });
  }

  function withTimeout(promise, ms) {
    return Promise.race([
      promise,
      new Promise(function (resolve) { setTimeout(function () { resolve(false); }, ms); })
    ]);
  }

  /* ---------------- checkout interception ---------------- */

  function initCheckoutGuard() {
    // Cart form "Check out" submit button.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.action || form.action.indexOf('/cart') === -1) return;
      var submitter = e.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      if (form.dataset.gbsTiersDone === '1') { delete form.dataset.gbsTiersDone; return; }
      e.preventDefault();
      withTimeout(enforceTiers(), 4000).then(function () {
        form.dataset.gbsTiersDone = '1';
        var h = document.createElement('input');
        h.type = 'hidden';
        h.name = 'checkout';
        form.appendChild(h);
        form.submit();
      });
    }, true);

    // Direct /checkout links (e.g. cart notification).
    document.addEventListener('click', function (e) {
      var link = e.target && e.target.closest && e.target.closest('a[href="/checkout"], a[href^="/checkout?"]');
      if (!link) return;
      e.preventDefault();
      var href = link.getAttribute('href');
      withTimeout(enforceTiers(), 4000).then(function () { location.href = href; });
    }, true);
  }

  /* ---------------- boot ---------------- */

  function boot() {
    initProductPage();
    initCheckoutGuard();
    if (location.pathname.indexOf('/cart') === 0) {
      withTimeout(enforceTiers(), 6000).then(function (changed) {
        if (changed) location.reload();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
